
import { Component, Vue, Prop } from 'vue-property-decorator';

/**
 * Badge type variants for different promotional offers
 */
export type BadgeType = 'FIRST_MONTH_SPECIAL' | 'BEST_VALUE' | 'TWO_MONTHS_FREE';

/**
 * DiscountBadge Component
 *
 * Displays promotional badges on pricing cards with different styles and
 * positioning based on offer type. Supports animation for attention-grabbing.
 *
 * @component
 * @example
 * ```vue
 * <!-- First month discount (time-limited) -->
 * <DiscountBadge
 *   v-if="shouldShowDiscountBadge(item)"
 *   type="FIRST_MONTH_SPECIAL"
 *   :visible="true"
 *   :animate="true"
 * />
 *
 * <!-- Yearly bonus (always-available) -->
 * <DiscountBadge
 *   v-if="isYearly"
 *   type="TWO_MONTHS_FREE"
 *   :visible="true"
 *   :animate="false"
 * />
 *
 * <!-- Premium plan (shows when no discount active) -->
 * <DiscountBadge
 *   v-if="isPremiumPlan && !hasDiscount"
 *   type="BEST_VALUE"
 *   :visible="true"
 * />
 * ```
 *
 * @remarks
 * Badge Types:
 * - FIRST_MONTH_SPECIAL: Time-limited monthly discount (🔥 fire emoji)
 * - TWO_MONTHS_FREE: Yearly billing bonus (🎁 gift emoji)
 * - BEST_VALUE: Premium plan highlight (⭐ star emoji)
 *
 * Positioning:
 * - FIRST_MONTH_SPECIAL: Centered at top
 * - TWO_MONTHS_FREE: Centered at top (same as monthly for consistency)
 * - BEST_VALUE: Top-right inside card
 *
 * Animation:
 * - Pulse animation draws attention to limited offers
 * - Different animations for centered vs right-positioned badges
 * - Animation optional via `animate` prop
 */
@Component({
  components: {},
})
export default class DiscountBadge extends Vue {
  /** Badge type variant to display */
  @Prop({ required: true })
  public type!: BadgeType;

  /** Whether badge should be visible */
  @Prop({ default: true })
  public visible!: boolean;

  /** Whether to apply pulse animation */
  @Prop({ default: false })
  public animate!: boolean;

  /**
   * Get badge icon emoji based on type
   *
   * @returns {string} Emoji icon character
   *
   * @remarks
   * Icon meanings:
   * - 🔥 (Fire): Urgency, time-limited offer
   * - ⭐ (Star): Premium quality, best choice
   * - 🎁 (Gift): Bonus, added value
   */
  get badgeIcon(): string {
    switch (this.type) {
      case 'FIRST_MONTH_SPECIAL':
        return '🔥';
      case 'BEST_VALUE':
        return '⭐';
      case 'TWO_MONTHS_FREE':
        return '🎁';
      default:
        return '🔥';
    }
  }

  /**
   * Get badge display text based on type
   *
   * @returns {string} Uppercase badge text
   *
   * @remarks
   * - All text uppercase for visual impact
   * - Short and clear messaging
   * - Emphasizes value proposition
   */
  get badgeText(): string {
    switch (this.type) {
      case 'FIRST_MONTH_SPECIAL':
        return 'FIRST MONTH SPECIAL';
      case 'BEST_VALUE':
        return 'BEST VALUE';
      case 'TWO_MONTHS_FREE':
        return '2 MONTHS FREE';
      default:
        return '';
    }
  }

  /**
   * Get CSS class for badge positioning and styling
   *
   * @returns {string} CSS class name for badge variant
   *
   * @remarks
   * Controls absolute positioning:
   * - badge-first-month: Centered at top (-12px)
   * - badge-two-months: Centered at top (-12px, same as first-month)
   * - badge-best-value: Top-right inside card
   *
   * Positioning ensures no badge overlap when switching billing periods.
   */
  get badgeTypeClass(): string {
    switch (this.type) {
      case 'FIRST_MONTH_SPECIAL':
        return 'badge-first-month';
      case 'BEST_VALUE':
        return 'badge-best-value';
      case 'TWO_MONTHS_FREE':
        return 'badge-two-months';
      default:
        return '';
    }
  }
}
